import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApolloClient } from '@apollo/client';
import { WishlistList } from 'packages/shared/definitions/types';

import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/tracking';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { AttributeTypes } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { css } from '@linaria/core';

import messages from '../messages';
import { PulsatingHeart } from '../PulsatingHeart';
import { useWishlistActions } from '../useWishlistActions';
import { useWishlistLists } from '../useWishlistLists';
import { useWishlistSelectionDrawer } from '../useWishlistSelectionDrawer';

import { Wrapper, StyledAddToWishlist } from './styled';
import { updateVariantWishlistedCache } from './updateVariantCache';

export const WishlistProductDetailIcon = () => {
  const { formatMessage } = useIntl();
  const { experiment_pd_wishlist_button_placement } = useFeatureFlags();
  const { currentVariant, product } = useProductDetailContext();
  const { webId: productId, wishlisted } = currentVariant;

  const { minimalPriceShown, rrpShown } = usePriceLogic(currentVariant);
  const { cache } = useApolloClient();
  const listsRef = React.useRef<WishlistList[]>([]);

  const { open } = useWishlistSelectionDrawer({
    onSave: ({ addedToLists, removedFromLists }) => {
      return handleWishlistClick({
        actionLocation: 'product_detail',
        trackingPromoLabels: [
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ].filter(Boolean) as string[],
        addToLists: addedToLists,
        removeFromLists: removedFromLists,
        lists: listsRef.current,
      });
    },
    interactionSource: 'product_add',
  });

  const { fetchLists } = useWishlistLists();

  const updateCallback = React.useCallback(
    (products: Array<{ productId: string; wishlisted: boolean }>) =>
      products.forEach((p) =>
        updateVariantWishlistedCache({
          wishlisted: p.wishlisted,
          productId: p.productId,
          cache,
        })
      ),
    [cache]
  );

  const { handleWishlistClick, loading } = useWishlistActions({
    productId: productId.toString(),
    onCompleted: updateCallback,
  });

  const handleClick = async () => {
    const lists = await fetchLists();
    listsRef.current = lists;
    if (lists.length === 1) {
      await handleWishlistClick({
        actionLocation: 'product_detail',
        trackingPromoLabels: [
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ].filter(Boolean) as string[],
        addToLists: wishlisted ? [] : [{ id: 'default' }],
        removeFromLists: wishlisted ? [{ id: 'default' }] : [],
        lists,
        notifyFailed: true,
      });
      return;
    }

    open({
      lists,
      productId,
      productName: `${product.brand?.name ?? ''} ${product.collection ?? ''}`,
    });
  };

  if (currentVariant.attributes[AttributeTypes.Damage]) {
    return null;
  }

  if (experiment_pd_wishlist_button_placement) {
    const wishlistButtonLabel = formatMessage(
      messages[wishlisted ? 'inWishlist' : 'addToWishlist']
    );

    return (
      <StyledAddToWishlist
        type="button"
        onClick={handleClick}
        aria-label={wishlistButtonLabel}
        className={css`
          width: 44px;
          height: 24px;
          justify-content: center;
          margin-top: 3px;
          padding: 0;
        `}
      >
        <PulsatingHeart
          wishlisted={!!wishlisted}
          loading={loading}
          className={css`
            padding: 0;
          `}
        />
      </StyledAddToWishlist>
    );
  }

  return (
    <Wrapper>
      <StyledAddToWishlist type="button" onClick={handleClick}>
        <PulsatingHeart wishlisted={!!wishlisted} loading={loading} />
        <FormattedMessage
          {...messages[`${wishlisted ? 'inWishlist' : 'addToWishlist'}`]}
        />
      </StyledAddToWishlist>
    </Wrapper>
  );
};
